import React, { useState, FormEvent, ChangeEvent } from 'react';
import { supabase } from '../supabase/supabaseClient';

interface FormData {
  // Basic Info
  owner_name: string;
  cafe_name: string;
  // Location
  address: string;
  city: string;
  state: string;
  country: string;
  google_maps_link: string;
  // Cafe Setup
  total_tables: string;
  open_hours: string;
  // Digital Bell Service
  interested_in_bell: boolean;
  wifi_available: boolean;
  // Contact
  phone_number: string;
  email: string;
  preferred_contact: 'phone' | 'email' | 'either';
  best_time: string;
  notes: string;
}

interface Message {
  text: string;
  type: 'success' | 'error' | '';
}

interface CafeOwnerFormProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const INITIAL_FORM: FormData = {
  owner_name: '',
  cafe_name: '',
  address: '',
  city: '',
  state: '',
  country: '',
  google_maps_link: '',
  total_tables: '',
  open_hours: '',
  interested_in_bell: true,
  wifi_available: true,
  phone_number: '',
  email: '',
  preferred_contact: 'either',
  best_time: '',
  notes: '',
};

function SectionHeading({ icon, title, subtitle }: { icon: React.ReactNode; title: string; subtitle?: string }) {
  return (
    <div className="flex items-center gap-3 pt-2 pb-1 border-t border-neutral-100">
      <div className="w-8 h-8 rounded-lg bg-neutral-900 text-white flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div>
        <p className="text-sm font-semibold text-neutral-900">{title}</p>
        {subtitle && <p className="text-xs text-neutral-400">{subtitle}</p>}
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer select-none group">
      <div
        onClick={() => onChange(!checked)}
        className={`relative w-10 h-5 rounded-full transition-colors duration-200 flex-shrink-0 ${checked ? 'bg-neutral-900' : 'bg-neutral-200'}`}
      >
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${checked ? 'translate-x-5' : ''}`} />
      </div>
      <span className="text-sm text-neutral-700 group-hover:text-neutral-900 transition-colors">{label}</span>
    </label>
  );
}

export function CafeOwnerForm({ isOpen, setIsOpen }: CafeOwnerFormProps) {
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<Message>({ text: '', type: '' });
  const [submitted, setSubmitted] = useState<boolean>(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const val = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setFormData(prev => ({ ...prev, [name]: val }));
  };

  const handleToggle = (field: keyof FormData) => (val: boolean) => {
    setFormData(prev => ({ ...prev, [field]: val }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ text: '', type: '' });
    try {
      const { error } = await supabase.from('cafe_owners').insert([formData]).select();
      if (error) throw error;
      setSubmitted(true);
    } catch (error) {
      setMessage({ text: `Something went wrong — please try again. (${(error as Error).message})`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => { setFormData(INITIAL_FORM); setMessage({ text: '', type: '' }); };

  const closeModal = () => {
    setIsOpen(false);
    setTimeout(() => { handleReset(); setSubmitted(false); }, 300);
  };

  const inp = 'w-full px-4 py-3 border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition text-sm placeholder:text-neutral-400 bg-white';
  const lbl = 'block text-sm font-medium text-neutral-900 mb-1.5';
  const num = inp + ' [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={closeModal}>
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[92vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="sticky top-0 bg-white/95 backdrop-blur-md border-b border-neutral-100 px-8 py-5 rounded-t-3xl flex items-start justify-between z-10">
              <div>
                <h2 className="text-xl font-semibold tracking-tight text-neutral-900">Register your cafe</h2>
                <p className="text-neutral-500 text-xs mt-0.5">Tell us about your space — we'll be in touch to get you set up.</p>
              </div>
              <button onClick={closeModal} className="text-neutral-400 hover:text-neutral-900 transition-colors mt-0.5 flex-shrink-0 p-1 rounded-lg hover:bg-neutral-100">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* Success */}
            {submitted ? (
              <div className="px-8 py-16 flex flex-col items-center text-center gap-4">
                <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mb-2">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                </div>
                <h3 className="text-xl font-semibold text-neutral-900">You're registered!</h3>
                <p className="text-neutral-500 text-sm max-w-sm leading-relaxed">
                  Thanks, <span className="font-medium text-neutral-800">{formData.owner_name || 'there'}</span>!{' '}
                  <span className="font-medium text-neutral-800">{formData.cafe_name}</span> in{' '}
                  <span className="font-medium text-neutral-800">{formData.city || formData.address}</span> is on our list.
                  {formData.interested_in_bell && <> Our team will reach out to walk you through your digital bell setup.</>}
                </p>
                <button onClick={closeModal} className="mt-4 bg-neutral-900 hover:bg-neutral-800 text-white text-sm font-medium py-3 px-8 rounded-full transition-all">Done</button>
              </div>
            ) : (

              <form onSubmit={handleSubmit} className="px-8 py-6 space-y-6">

                {/* ── 1. Basic Info ── */}
                <SectionHeading
                  icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>}
                  title="Basic Information"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="owner_name" className={lbl}>Owner / Manager Name *</label>
                    <input type="text" id="owner_name" name="owner_name" required value={formData.owner_name} onChange={handleChange} placeholder="e.g. Rahul Sharma" className={inp} />
                  </div>
                  <div>
                    <label htmlFor="cafe_name" className={lbl}>Cafe Name *</label>
                    <input type="text" id="cafe_name" name="cafe_name" required value={formData.cafe_name} onChange={handleChange} placeholder="e.g. Chai Adda" className={inp} />
                  </div>
                </div>

                {/* ── 2. Location ── */}
                <SectionHeading
                  icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>}
                  title="Location"
                  subtitle="Where is your cafe located?"
                />
                <div>
                  <label htmlFor="address" className={lbl}>Street Address</label>
                  <input type="text" id="address" name="address" value={formData.address} onChange={handleChange} placeholder="e.g. 12, MG Road, Koramangala" className={inp} />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="col-span-2">
                    <label htmlFor="city" className={lbl}>City *</label>
                    <input type="text" id="city" name="city" required value={formData.city} onChange={handleChange} placeholder="e.g. Bengaluru" className={inp} />
                  </div>
                  <div>
                    <label htmlFor="state" className={lbl}>State / Region</label>
                    <input type="text" id="state" name="state" value={formData.state} onChange={handleChange} placeholder="e.g. Karnataka" className={inp} />
                  </div>
                  <div>
                    <label htmlFor="country" className={lbl}>Country</label>
                    <input type="text" id="country" name="country" value={formData.country} onChange={handleChange} placeholder="e.g. India" className={inp} />
                  </div>
                </div>
                <div>
                  <label htmlFor="google_maps_link" className={lbl}>Google Maps Link</label>
                  <input type="url" id="google_maps_link" name="google_maps_link" value={formData.google_maps_link} onChange={handleChange} placeholder="https://maps.google.com/..." className={inp} />
                </div>

                {/* ── 3. Cafe Setup ── */}
                <SectionHeading
                  icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18M10 4v16M14 4v16" /></svg>}
                  title="Cafe Setup"
                  subtitle="Your space and table layout"
                />
                <div className="max-w-[160px]">
                  <label htmlFor="total_tables" className={lbl}>How many tables do you have?</label>
                  <input type="number" id="total_tables" name="total_tables" min="1" value={formData.total_tables} onChange={handleChange} placeholder="e.g. 12" className={num} />
                </div>
                <div>
                  <label htmlFor="open_hours" className={lbl}>Opening Hours</label>
                  <input type="text" id="open_hours" name="open_hours" value={formData.open_hours} onChange={handleChange} placeholder="e.g. Mon–Sat 9am–11pm, Sun 10am–10pm" className={inp} />
                </div>

                {/* ── 4. Digital Bell Service ── */}
                <SectionHeading
                  icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>}
                  title="Digital Bell Service"
                  subtitle="Let guests call staff from their table — no app needed"
                />
                <Toggle checked={formData.interested_in_bell} onChange={handleToggle('interested_in_bell')} label="I'm interested in the digital bell service" />

                {formData.interested_in_bell && (
                  <div>
                    <Toggle checked={formData.wifi_available} onChange={handleToggle('wifi_available')} label="WiFi available throughout cafe" />
                  </div>
                )}

                {/* ── 5. Contact ── */}
                <SectionHeading
                  icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>}
                  title="Contact Preferences"
                  subtitle="How should we reach you to follow up?"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="phone_number" className={lbl}>Phone Number</label>
                    <input type="tel" id="phone_number" name="phone_number" value={formData.phone_number} onChange={handleChange} placeholder="e.g. +91 98765 43210" className={inp} />
                  </div>
                  <div>
                    <label htmlFor="email" className={lbl}>Email Address</label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} placeholder="e.g. rahul@chaiadda.in" className={inp} />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="preferred_contact" className={lbl}>Preferred Contact Method</label>
                    <select id="preferred_contact" name="preferred_contact" value={formData.preferred_contact} onChange={handleChange} className={inp + ' cursor-pointer'}>
                      <option value="either">No preference</option>
                      <option value="phone">Phone call</option>
                      <option value="email">Email</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="best_time" className={lbl}>Best Time to Reach You</label>
                    <input type="text" id="best_time" name="best_time" value={formData.best_time} onChange={handleChange} placeholder="e.g. Weekdays after 2pm" className={inp} />
                  </div>
                </div>
                <div>
                  <label htmlFor="notes" className={lbl}>Anything else we should know?</label>
                  <textarea id="notes" name="notes" rows={3} value={formData.notes} onChange={handleChange} placeholder="e.g. We have a rooftop seating area and need help with setup…" className={inp + ' resize-none'} />
                </div>

                {/* Error */}
                {message.text && message.type === 'error' && (
                  <div className="p-4 rounded-xl text-sm border bg-red-50 text-red-800 border-red-200">{message.text}</div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-2 pb-2">
                  <button type="submit" disabled={loading}
                    className="flex-1 bg-neutral-900 hover:bg-neutral-800 text-white font-medium py-3.5 px-6 rounded-full transition-all disabled:bg-neutral-400 disabled:cursor-not-allowed text-sm hover:shadow-lg">
                    {loading ? 'Submitting…' : 'Submit & Request Callback'}
                  </button>
                  <button type="button" onClick={handleReset}
                    className="px-6 py-3.5 border-2 border-neutral-200 hover:border-neutral-900 text-neutral-900 text-sm font-medium rounded-full transition-all hover:bg-neutral-50">
                    Clear
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}